import { useCallback } from 'react'

import { AxiosError, AxiosResponse } from 'axios'

import { ConvertUtility } from '../utils/ConvertUtility'
import { useNavigate } from 'react-router-dom'
import { Configuration } from '../openapi/configuration'

export type ApiError = {
  statusCode: number
  data: object | null
}

export interface ApiResult<T> {
  success: T | null
  error: ApiError | null
}

export interface CallApiOptions {
  /** 背景更新の失敗では、開いているエディタを離れない */
  redirectOnError?: boolean
}

export const useCallApi = () => {
  const navigate = useNavigate()
  const { convertKeysToCamel, convertKeysToSnake } = ConvertUtility()

  const callApi = useCallback(
    async function <TResponse, TParams>(
      params: TParams,
      fn: (params: TParams) => Promise<AxiosResponse>,
      { redirectOnError = true }: CallApiOptions = {},
    ): Promise<ApiResult<TResponse>> {
      let response = undefined
      try {
        response = await fn(convertKeysToSnake(params))
        response = convertKeysToCamel(response)
        return { success: response.data as TResponse, error: null }
      } catch (e: AxiosError | unknown) {
        if (e instanceof AxiosError) {
          const statusCode = e.response?.status
          if (statusCode && redirectOnError) {
            if (statusCode === 401) {
              navigate(`/error`)
            }
            if (statusCode !== 200) {
              navigate(`/error`)
            }
          }
          if (e.code === 'ERR_NETWORK') {
            // navigate(`/error/network`);
          }
          return {
            success: null,
            error: { statusCode: statusCode!, data: e.response?.data },
          }
        } else {
          throw e
        }
      }
    },
    [convertKeysToCamel, convertKeysToSnake, navigate],
  )

  const defaultConfiguration = useCallback(async () => {
    const env = import.meta.env
    const configuration = new Configuration()
    configuration.basePath = env.VITE_DIGITANE_API_ENDPOINT
    configuration.baseOptions = {
      headers: {
        'X-Application-Token': env.VITE_DIGITANE_API_KEY,
      },
      withCredentials: true,
    }
    return configuration
  }, [])

  return { callApi, defaultConfiguration }
}
